import { useEffect, useState } from "react";
import { CLIENT_COLORS } from "../constants";
import { Dot, MARCA_CLARA } from "./primitives";

function temaAtual() {
  const t = document.documentElement.getAttribute("data-theme");
  if (t === "light" || t === "dark") return t;
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function Sidebar({ view, setView, clients, onLogout }) {
  const [aberto, setAberto] = useState(false);
  const [tema, setTema] = useState(() => (typeof document !== "undefined" ? temaAtual() : "light"));

  useEffect(() => {
    if (!aberto) return;
    const h = (e) => e.key === "Escape" && setAberto(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [aberto]);

  function ir(id) { setView(id); setAberto(false); }

  function trocarTema() {
    const novo = tema === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", novo);
    try { localStorage.setItem("rc2_tema", novo); } catch (e) { /* sem armazenamento */ }
    setTema(novo);
  }

  const nav = [
    { id: "agenda",       label: "Agenda",        icon: "◷" },
    { id: "rotina-do-dia", label: "Rotina do Dia", icon: "◉" },
    { id: "dashboard",    label: "Dashboard",    icon: "⬡" },
    { id: "performance",  label: "Performance",   icon: "◈" },
    { id: "criativos",    label: "Criativos",     icon: "◻" },
    { id: "concorrentes", label: "Concorrentes",  icon: "◎" },
    { id: "roteiros",     label: "Roteiros",      icon: "◈" },
    { id: "hooks",        label: "Hooks",         icon: "⚓" },
    { id: "prompts",      label: "Prompts",       icon: "⊟" },
    { id: "claude-design", label: "Claude Design", icon: "✦" },
    { id: "prospect",     label: "Prospect",      icon: "◎" },
    { id: "social-media", label: "Social Media",  icon: "◻" },
    { id: "biblioteca",   label: "Como Usar",     icon: "◉" },
    { id: "skills",       label: "Skills",        icon: "⌘" },
    { id: "crm-medico",   label: "CRM Médico",    icon: "🏥" },
    { id: "automacoes",   label: "Automações",    icon: "⚡" },
  ];

  const internos = clients.filter((c) => c.status === "Ativo" && c.type === "Interno");
  const ativos   = clients.filter((c) => c.status === "Ativo" && c.type !== "Interno");

  const Item = ({ id, children, style }) => (
    <button
      type="button"
      className={`nav-item${view === id ? " active" : ""}`}
      aria-current={view === id ? "page" : undefined}
      onClick={() => ir(id)}
      style={style}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Barra do celular (só aparece até 760px) */}
      <div className="mobile-bar">
        <img src={MARCA_CLARA} alt="RC2Clicks" />
        <button type="button" onClick={() => setAberto(true)} aria-expanded={aberto} aria-controls="rc2-menu">Menu</button>
      </div>
      <button type="button" className={`side-backdrop${aberto ? " open" : ""}`} aria-label="Fechar menu" tabIndex={aberto ? 0 : -1} onClick={() => setAberto(false)} />

      <nav id="rc2-menu" className={`sidebar${aberto ? " open" : ""}`} style={{ height: "100vh", userSelect: "none" }} aria-label="Menu principal">

        {/* Logomarca 2026 (versão clara: o menu é escuro) */}
        <div className="side-brand">
          <img src={MARCA_CLARA} alt="RC2Clicks" />
        </div>

        <div className="side-scroll">

          <div className="nav-group">Menu</div>
          {nav.map((n) => (
            <Item key={n.id} id={n.id}>
              <span style={{ fontSize: 13 }} aria-hidden="true">{n.icon}</span>
              {n.label}
            </Item>
          ))}

          <div className="nav-group">Produtos</div>
          <Item id="fluxomed">
            <span style={{ fontSize: 13 }} aria-hidden="true">⚕</span>
            FluxoMed
          </Item>

          {internos.length > 0 && (
            <>
              <div className="nav-group">Interno</div>
              {internos.map((c) => (
                <Item key={c.id} id={`client:${c.id}`}>
                  <span style={{ fontSize: 10 }} aria-hidden="true">⬡</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </span>
                </Item>
              ))}
            </>
          )}

          <div className="nav-group">Clientes</div>
          {ativos.map((c) => {
            const col = CLIENT_COLORS[c.type] || CLIENT_COLORS["Interno"];
            return (
              <Item key={c.id} id={`client:${c.id}`}>
                <Dot color={col.dot} size={7} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.name}
                </span>
              </Item>
            );
          })}

          <Item id="clients" style={view === "clients" ? { marginTop: 4 } : { color: "var(--sidebar-faint)", marginTop: 4 }}>
            + Todos os clientes
          </Item>

        </div>

        {/* Rodapé */}
        <div className="side-foot" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 10, color: "var(--sidebar-faint)", letterSpacing: ".04em", fontFamily: "var(--font-data)" }}>
            rc2@crm · v3.1
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={trocarTema}
              title={tema === "dark" ? "Usar tema claro" : "Usar tema escuro"}
              aria-label={tema === "dark" ? "Usar tema claro" : "Usar tema escuro"}
              style={{ fontSize: 12, color: "var(--sidebar-muted)", cursor: "pointer", background: "rgb(255 255 255 / .06)", border: "1px solid var(--sidebar-line)", borderRadius: 999, padding: "4px 10px", fontFamily: "var(--font-data)" }}
            >
              {tema === "dark" ? "☀ claro" : "☾ escuro"}
            </button>
            <button
              type="button"
              onClick={onLogout}
              title="Sair"
              aria-label="Sair"
              style={{ fontSize: 12, color: "var(--sidebar-muted)", cursor: "pointer", background: "rgb(255 255 255 / .06)", border: "1px solid var(--sidebar-line)", borderRadius: 999, padding: "4px 10px" }}
            >
              ⏻
            </button>
          </span>
        </div>

      </nav>
    </>
  );
}
